import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  deleteOrgLogo,
  getAdminOrgProfile,
  updateOrgProfile,
  uploadOrgLogo,
  type OrganizationProfileAdmin,
} from '../lib/api/org-profile';
import { showErrorToast, showSuccessToast } from '../lib/toast';
import { ApiError } from '../lib/api/core';

// Story 064 — org admin edits the tenant's organization public profile.
// Org-admin-only, gated by AdminRoute. Mirrors the director profile
// avatar upload UX (file input + camera input + Upload / Take photo /
// Remove buttons) — single image slot for the org logo.

// Simple email-format check used by the inline form validation. The
// authoritative check still lives on the backend DTO (@IsEmail).
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function kebab(camel: string): string {
  return camel.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

const EMAIL_FIELD_KEYS = [
  'emailFromName',
  'emailFromAddress',
  'emailReplyTo',
  'emailListUnsubscribe',
] as const;
type EmailFieldKey = (typeof EMAIL_FIELD_KEYS)[number];

export function AdminOrganizationProfilePage() {
  const { t } = useTranslation('director');
  const [profile, setProfile] = useState<OrganizationProfileAdmin | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Story 092 — four email-identity fields, edited by the org admin.
  const [emailFromName, setEmailFromName] = useState('');
  const [emailFromAddress, setEmailFromAddress] = useState('');
  const [emailReplyTo, setEmailReplyTo] = useState('');
  const [emailListUnsubscribe, setEmailListUnsubscribe] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<EmailFieldKey, string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function applyProfile(p: OrganizationProfileAdmin) {
    setProfile(p);
    setName(p.name);
    setDescription(p.description ?? '');
    setEmailFromName(p.emailFromName ?? '');
    setEmailFromAddress(p.emailFromAddress ?? '');
    setEmailReplyTo(p.emailReplyTo ?? '');
    setEmailListUnsubscribe(p.emailListUnsubscribe ?? '');
    setFieldErrors({});
  }

  async function load() {
    setLoading(true);
    try {
      const p = await getAdminOrgProfile();
      applyProfile(p);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : t('admin.org_profile.load_failed_toast');
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // t intentionally omitted — stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    // Client-side validation for the three email fields. Empty string
    // is fine (it'll be persisted as NULL — "not configured"); only
    // non-empty values must look like an email.
    const errs: Partial<Record<EmailFieldKey, string>> = {};
    const fromAddrTrimmed = emailFromAddress.trim();
    const replyToTrimmed = emailReplyTo.trim();
    const listUnsubTrimmed = emailListUnsubscribe.trim();
    if (fromAddrTrimmed && !looksLikeEmail(fromAddrTrimmed)) {
      errs.emailFromAddress = t('admin.org_profile.email_invalid_error');
    }
    if (replyToTrimmed && !looksLikeEmail(replyToTrimmed)) {
      errs.emailReplyTo = t('admin.org_profile.email_invalid_error');
    }
    if (listUnsubTrimmed && !looksLikeEmail(listUnsubTrimmed)) {
      errs.emailListUnsubscribe = t('admin.org_profile.email_invalid_error');
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const updated = await updateOrgProfile({
        name: name.trim(),
        description: description.trim() || null,
        emailFromName: emailFromName.trim() || null,
        emailFromAddress: fromAddrTrimmed || null,
        emailReplyTo: replyToTrimmed || null,
        emailListUnsubscribe: listUnsubTrimmed || null,
      });
      applyProfile(updated);
      showSuccessToast(t('admin.org_profile.save_success_toast'));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : t('admin.org_profile.save_failed_toast');
      showErrorToast(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoFile(file: File | null) {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const updated = await uploadOrgLogo(file);
      applyProfile(updated);
      showSuccessToast(t('admin.org_profile.logo_uploaded_toast'));
    } catch (err) {
      showErrorToast(
        err instanceof Error
          ? err.message
          : t('admin.org_profile.logo_upload_failed_toast'),
      );
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleLogoRemove() {
    if (!profile?.logoUrl) return;
    setUploadingLogo(true);
    try {
      const updated = await deleteOrgLogo();
      applyProfile(updated);
      showSuccessToast(t('admin.org_profile.logo_removed_toast'));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : t('admin.org_profile.logo_remove_failed_toast');
      showErrorToast(message);
    } finally {
      setUploadingLogo(false);
    }
  }

  // Story 092 — derive the missing-fields list straight from the
  // persisted profile, not the local form state, so the banner reflects
  // what's actually configured server-side (not what the operator is
  // currently typing).
  const missingEmailFields: EmailFieldKey[] = profile
    ? EMAIL_FIELD_KEYS.filter((k) => !profile[k] || profile[k] === '')
    : [];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-2xl px-6 py-8">
        <h1 className="mb-6 text-2xl font-semibold">{t('admin.org_profile.title')}</h1>

        {loading ? (
          <p className="text-muted-foreground">{t('admin.org_profile.loading')}</p>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">
            {/* Story 092 — banner listing missing email-identity fields */}
            {missingEmailFields.length > 0 && (
              <div
                data-testid="org-profile-email-missing-banner"
                className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
              >
                <p className="font-medium">
                  {t('admin.org_profile.email_missing_banner_title')}
                </p>
                <ul className="mt-1 list-inside list-disc">
                  {missingEmailFields.map((key) => (
                    <li
                      key={key}
                      data-testid={`org-profile-email-missing-${kebab(key)}`}
                    >
                      {t(`admin.org_profile.email_field.${key}.label`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Logo */}
            <section className="space-y-2">
              <Label>{t('admin.org_profile.logo_label')}</Label>
              <div className="flex items-center gap-4">
                {profile?.logoUrl ? (
                  <img
                    data-testid="org-profile-logo"
                    src={profile.logoUrl}
                    alt={t('admin.org_profile.logo_alt')}
                    className="h-24 w-24 rounded border bg-muted/30 object-contain"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded border text-xs text-muted-foreground">
                    {t('admin.org_profile.logo_placeholder')}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    data-testid="org-profile-logo-input"
                    className="hidden"
                    onChange={(e) =>
                      handleLogoFile(e.target.files?.[0] ?? null)
                    }
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="org-profile-logo-upload"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {profile?.logoUrl
                      ? t('admin.org_profile.logo_replace')
                      : t('admin.org_profile.logo_upload')}
                  </Button>
                  {profile?.logoUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid="org-profile-logo-remove"
                      onClick={handleLogoRemove}
                      disabled={uploadingLogo}
                    >
                      {t('admin.org_profile.logo_remove')}
                    </Button>
                  )}
                </div>
              </div>
            </section>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="org-name">{t('admin.org_profile.name_label')}</Label>
              <Input
                id="org-name"
                data-testid="org-profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
                required
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="org-description">{t('admin.org_profile.description_label')}</Label>
              <textarea
                id="org-description"
                data-testid="org-profile-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={saving}
                rows={4}
                className="flex w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
              />
            </div>

            {/* Story 092 — email identity section */}
            <section
              className="space-y-4 rounded border bg-muted/20 p-4"
              data-testid="org-profile-email-section"
            >
              <header>
                <h2 className="text-base font-medium">
                  {t('admin.org_profile.email_section_title')}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {t('admin.org_profile.email_section_hint')}
                </p>
              </header>

              <div className="space-y-2">
                <Label htmlFor="org-email-from-name">
                  {t('admin.org_profile.email_field.emailFromName.label')}
                </Label>
                <Input
                  id="org-email-from-name"
                  data-testid="org-profile-email-from-name-input"
                  value={emailFromName}
                  onChange={(e) => setEmailFromName(e.target.value)}
                  disabled={saving}
                  maxLength={120}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="org-email-from-address">
                  {t('admin.org_profile.email_field.emailFromAddress.label')}
                </Label>
                <Input
                  id="org-email-from-address"
                  type="text"
                  inputMode="email"
                  data-testid="org-profile-email-from-address-input"
                  value={emailFromAddress}
                  onChange={(e) => setEmailFromAddress(e.target.value)}
                  disabled={saving}
                  maxLength={254}
                />
                {fieldErrors.emailFromAddress && (
                  <p
                    data-testid="org-profile-email-from-address-error"
                    className="text-xs text-destructive"
                  >
                    {fieldErrors.emailFromAddress}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="org-email-reply-to">
                  {t('admin.org_profile.email_field.emailReplyTo.label')}
                </Label>
                <Input
                  id="org-email-reply-to"
                  type="text"
                  inputMode="email"
                  data-testid="org-profile-email-reply-to-input"
                  value={emailReplyTo}
                  onChange={(e) => setEmailReplyTo(e.target.value)}
                  disabled={saving}
                  maxLength={254}
                />
                {fieldErrors.emailReplyTo && (
                  <p
                    data-testid="org-profile-email-reply-to-error"
                    className="text-xs text-destructive"
                  >
                    {fieldErrors.emailReplyTo}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="org-email-list-unsubscribe">
                  {t('admin.org_profile.email_field.emailListUnsubscribe.label')}
                </Label>
                <Input
                  id="org-email-list-unsubscribe"
                  type="text"
                  inputMode="email"
                  data-testid="org-profile-email-list-unsubscribe-input"
                  value={emailListUnsubscribe}
                  onChange={(e) => setEmailListUnsubscribe(e.target.value)}
                  disabled={saving}
                  maxLength={254}
                />
                {fieldErrors.emailListUnsubscribe && (
                  <p
                    data-testid="org-profile-email-list-unsubscribe-error"
                    className="text-xs text-destructive"
                  >
                    {fieldErrors.emailListUnsubscribe}
                  </p>
                )}
              </div>
            </section>

            <Button
              type="submit"
              data-testid="org-profile-save"
              disabled={saving}
            >
              {saving
                ? t('admin.org_profile.saving')
                : t('admin.org_profile.save_button')}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
